import {defineStore} from 'pinia';
import {AxiosError} from "axios";
import schoolApi from "@/services/school_api.ts";
import {
    SchoolSchema,
    SchoolTypeSchema,
    SchoolAddressSchema,
    SchoolContactSchema,
    SchoolPreferenceSchema,
    SchoolInfoUpdate,
    SubscriptionUsageSchema,
    SubscriptionPlanSchema
} from '@/types/school_schema.ts';
// import Cookies from "js-cookie";
import { mapBackendError } from '@/lib/utils.ts';
import useFeatureAccess from '@/composables/useFeatureAccess.ts';

export const useSchoolStore = defineStore('school', {
    state: () => ({
        isLoading: false,
        schools: [] as SchoolSchema[],
        error: '',
        isLoadingAction: false,
        errorAction: '',
        selectedSchool: {} as SchoolSchema,
        schoolInfo: {} as SchoolSchema,
        schoolTypes: [] as SchoolTypeSchema[],
        subscriptionPlan: {} as SubscriptionPlanSchema,
        subscriptionUsage: {} as SubscriptionUsageSchema,
    }),
    actions: {
        async fetchAllSchools() {
            try {
                const response = await schoolApi.getAllSchool();
                if (response.data.data != null && response.data.data.length > 0) {
                    this.schools = response.data.data;

                    // The list carries freshly signed logo URLs (valid 7 days); the
                    // cached selected school keeps whatever was signed when it was
                    // picked. Re-sync it so the sidebar never holds an expired link.
                    const current: SchoolSchema = this.selectedSchool?.id
                        ? this.selectedSchool
                        : JSON.parse(localStorage.getItem('school') || '{}');
                    const fresh = this.schools.find(school => String(school.id) === String(current.id));
                    if (fresh) {
                        this.selectedSchool = { ...current, ...fresh };
                        localStorage.setItem('school', JSON.stringify(this.selectedSchool));
                    }
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async fetchSchoolById() {
            try {
                const id = this.selectedSchool.id
                const response = await schoolApi.getSchoolById(id);
                if (response.data.data != null) {
                    this.schoolInfo = response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        getSelectedSchool() {
            this.selectedSchool = JSON.parse(localStorage.getItem('school') || '{}');
        },
        async selectSchool(schoolId: number) {
            try {
                // No API call needed - school selection is now header-based only
                const school = this.schools.filter(school => school.id === schoolId)[0];
                this.selectedSchool = school;
                localStorage.setItem('school', JSON.stringify(school));

                // Feature entitlements are scoped to a school. Clear the old
                // school's singleton cache before fetching the new resolution.
                const featureAccess = useFeatureAccess();
                featureAccess.clearCache();
                try {
                    await featureAccess.fetchFeatures(true);
                } catch (featureError) {
                    console.log('Could not fetch features after school selection:', featureError);
                }

                return true;
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async fetchSchoolTypes() {
            try {
                const response = await schoolApi.getSchoolTypes();
                this.schoolTypes = response.data.data;
            } catch (err) {
                console.log(err);
                throw mapBackendError(err as AxiosError);
            }
        },
        async createSchoolAddress(input: SchoolAddressSchema) {
            try {
                const id = this.selectedSchool.id
                const response = await schoolApi.createSchoolAddress(id, input);
                if (response.data.data != null) {
                    return response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async createSchoolContact(input: SchoolContactSchema) {
            try {
                const id = this.selectedSchool.id
                const response = await schoolApi.createSchoolContact(id, input);
                if (response.data.data != null) {
                    return response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async createSchoolPreference(input: SchoolPreferenceSchema) {
            try {
                const id = this.selectedSchool.id
                const response = await schoolApi.createSchoolPreference(id, input);
                if (response.data.data != null) {
                    return response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async updateSchoolPreference(id: number | string, update: SchoolPreferenceSchema) {
            try {
                const schoolId = this.selectedSchool.id
                await schoolApi.updateSchoolPreference(Number(id), schoolId, update);
                if (this.selectedSchool && this.selectedSchool.id === schoolId) {
                    this.selectedSchool.preference = update;
                    localStorage.setItem('school', JSON.stringify(this.selectedSchool));
                    window.dispatchEvent(new Event('timezone-updated')); // Optional, good for reactivity
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async updateSchoolInfo(update: SchoolInfoUpdate) {
            try {
                await schoolApi.updateSchoolInfo(update);

                // An undefined field means "leave as-is" (the backend COALESCEs it),
                // so it must not be spread into the caches or it blanks the old value.
                let patch: Record<string, unknown> = Object.fromEntries(
                    Object.entries(update).filter(([, v]) => v !== undefined)
                );

                // The form sends the logo as a stored object key, which an <img>
                // cannot load; reads return a signed URL. Take the saved values
                // back from the API so every cache gets a displayable logo.
                try {
                    const response = await schoolApi.getSchoolById(Number(this.selectedSchool?.id ?? this.schoolInfo.id));
                    const fresh = response.data.data as Record<string, unknown> | null;
                    if (fresh) {
                        patch = Object.fromEntries(
                            Object.entries(patch).map(([key, value]) => [key, fresh[key] ?? value])
                        );
                    }
                } catch {
                    // Keep the form values; the next school-list load re-signs the logo.
                }

                // Name/logo are cached in three places: schoolInfo (settings page),
                // selectedSchool + localStorage (ID cards, reports), and the schools
                // list (switch-school menu). Patch all of them or the UI keeps
                // showing the old name until a re-login.
                this.schoolInfo = { ...this.schoolInfo, ...patch };

                // The endpoint updates whichever school the tenant header points at,
                // i.e. always selectedSchool — so patch it unconditionally. Comparing
                // ids here is unreliable: /schools/user and /schools/:id disagree on
                // whether id is a string or a number, so === silently no-ops.
                const updatedId = String(this.selectedSchool?.id ?? this.schoolInfo.id);
                this.selectedSchool = { ...this.selectedSchool, ...patch };
                localStorage.setItem('school', JSON.stringify(this.selectedSchool));

                this.schools = this.schools.map(school =>
                    String(school.id) === updatedId ? { ...school, ...patch } : school
                );
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async updateSchoolContact(id: number | string, update: SchoolContactSchema) {
            try {
                const schoolId = this.selectedSchool.id
                await schoolApi.updateSchoolContact(Number(id), schoolId, update);
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async updateSchoolAddress(id: number | string, update: SchoolAddressSchema) {
            try {
                const schoolId = this.selectedSchool.id
                await schoolApi.updateSchoolAddress(Number(id), schoolId, update);
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async getSubscriptionPlanOfSchool() {
            try {
                const schoolId = this.selectedSchool.id
                const response = await schoolApi.getSubscriptionPlanOfSchool(schoolId);
                if (response.data.data != null) {
                    this.subscriptionPlan = response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        },
        async getSchoolUsageOfPlan() {
            try {
                const schoolId = this.selectedSchool.id
                const response = await schoolApi.getSchoolUsageOfPlan(schoolId);
                if (response.data.data != null) {
                    this.subscriptionUsage = response.data.data;
                }
            } catch (err) {
                throw mapBackendError(err as AxiosError);
            }
        }
    },
});
